import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { setSnackbar } from '../../../redux/actions';

import BreadCrumbArrow from '../../../images/icons/breadcrumb-arrow.svg';
import { session } from '../../../services/session';
import LockIcon from '../../merchant/strategies/admin-lock.svg';
import EyeIcon from '../../../images/icons/public-eye.svg';
import Product from '../../publisher/trends/cart-explorer/product';
import ProductDropdown from './product-dropdown';

import CollectionsService from './collections-service';

import './styles/create-container.scss';

const sections = ['Collection Details', 'Add Products', 'Availability'];

const availabilityOptions = [
  {
    label: 'Public',
    description: `If set to public, this collection (draft or completed) will be visible to
			Publishers on the New Rates Page.`,
    icon: EyeIcon,
  },
  {
    label: 'Admin Only',
    description:
      'This collection (draft or completed) will only be visible to you and your internal team.',
    icon: LockIcon,
  },
  {
    label: 'Private',
    description: 'If set to private, only you can see this collection.',
    icon: LockIcon,
  },
];

const CreateCollection = ({ match, history, setSnackbar }) => {
  const searchBarDescription =
    match.params.creationType === 'manual'
      ? 'Search by brand and add products to your collection manually.'
      : `All products matching the following brands will be added to this collection automatically.
			Products will be updated automatically.`;

  const searchType = match.params.creationType === 'manual' ? 'product' : 'brand';

  const [didMount, setDidMount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [productParams, setProductParams] = useState({
    search_query: productQuery,
    search_type: searchType,
  });
  const [createParams, setCreateParams] = useState({});
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [availability, setAvailability] = useState(availabilityOptions[0].label);
  const [mockData, setMockData] = useState(false);

  useEffect(() => {
    setDidMount(true);
  }, []);

  useEffect(() => {
    if (didMount) {
      setProductParams({ search_query: productQuery, search_type: searchType });
    }
  }, [productQuery]);

  useEffect(() => {
    if (didMount) {
      setCreateParams(prevState => {
        return {
          ...prevState,
          name,
          description,
          canonical_product_ids: formatProductIds(),
        };
      });
    }
  }, [name, description, selectedProducts]);

  useEffect(() => {
    if (didMount) {
      if (mockData) {
        setProducts(formatProductData(CollectionsService.getMockProducts().data));
      } else {
        getProducts();
      }
    }
  }, [mockData, productParams]);

  async function getProducts() {
    try {
      const resp = await CollectionsService.getProducts(productParams);
      setProducts(formatProductData(resp.data.data));
    } catch {
      setProducts([]);
    }
  }

  function formatProductData(data) {
    return data.map(canonical => {
      const productUrl = canonical.product_urls.length ? canonical.product_urls[0] : null;
      return { canonical_product: canonical.canonical_product, ...productUrl };
    });
  }

  function formatProductIds() {
    const productIdMap = {};
    selectedProducts.forEach(product => {
      productIdMap[product.canonical_product.id] = String(product.merchant_product_id);
    });
    return JSON.stringify(productIdMap);
  }

  function getSection(idx) {
    let form;
    if (!idx) {
      form = (
        <div className="form">
          <div className="form-item">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Collection Name"
            />
          </div>
          <div className="form-item">
            <label>
              <p>Description</p>(optional)
            </label>
            <input
              type="text"
              className="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Collection Description"
            />
          </div>
          <div className="form-item cover-image">
            <label>Cover Image</label>
            <input
              type="text"
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              placeholder="TODO"
            />
          </div>
        </div>
      );
    } else if (idx === 1) {
      form = (
        <div className="form">
          <div className="search-bar-title">{`${match.params.creationType} Collection`}</div>
          <div className="search-bar-description">{searchBarDescription}</div>
          <ProductDropdown
            setSearchQuery={setProductQuery}
            searchQuery={productQuery}
            placeholder="Search for a Brand"
            results={products}
            setProducts={setProducts}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            creationType={match.params.creationType}
          />
          <div className="products">
            {selectedProducts && selectedProducts.length ? (
              <>
                <div className="products-amount">{`${selectedProducts.length} products added`}</div>
                <div className="products-list">
                  {selectedProducts.map((result, index) => (
                    <Product key={index} product={result} type="collections" />
                  ))}
                </div>
              </>
            ) : (
              <div className="no-products">No products added yet.</div>
            )}
          </div>
        </div>
      );
    } else if (idx === 2) {
      form = (
        <div className="form">
          {availabilityOptions.map((option, index) => {
            const selected = availability === option.label;
            return (
              <div
                key={index}
                className={`radio-form-item ${selected ? 'selected' : ''}`}
                onClick={() => setAvailability(option.label)}
              >
                <div className="input-label-icon">
                  <span className={`icon ${selected ? 'selected' : ''}`}>
                    <span className="fill"></span>
                  </span>
                  <label>{option.label}</label>
                  <img src={option.icon} alt="availability" />
                </div>
                <p>{option.description}</p>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div
        className={`section ${sections[idx]
          .toLowerCase()
          .split(' ')
          .join('-')}`}
        key={idx}
      >
        <div className="name-icon">
          <span className="idx-icon">{idx + 1}</span>
          <h2>{sections[idx]}</h2>
        </div>
        {form}
      </div>
    );
  }

  async function handleCreate() {
    try {
      await CollectionsService.createCollection(createParams);
      history.push('/v2/admin/collections');
    } catch {
      setSnackbar('Creation failed, try again.');
    }
  }

  const mocksButton = session.isAdminView ? (
    <button className="mocks-btn" onClick={() => setMockData(!mockData)}>
      Mocks
    </button>
  ) : null;

  return (
    <div className="create-collection-container">
      <div className="navigation">
        <Link to="/v2/admin/collections">Collections</Link>
        <img src={BreadCrumbArrow} alt="arrow" className="breadcrumb-arrow" />
        <span>Create Collection</span>
      </div>
      {mocksButton}
      <h1>Create Collection</h1>
      <div className="section-names">
        {sections.map((section, idx) => (
          <div className="name-icon" key={idx}>
            <span className="idx-icon">{idx + 1}</span>
            <h2>{sections[idx]}</h2>
          </div>
        ))}
      </div>
      <div className="sections">{sections.map((section, idx) => getSection(idx))}</div>
      <div className="controls">
        <Link to="/v2/admin/collections">
          <button className="cancel">Cancel</button>
        </Link>
        <button className="save">Save Draft</button>
        <button className="create" onClick={handleCreate}>
          Create Collection
        </button>
      </div>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    setSnackbar: msg => dispatch(setSnackbar(msg)),
  };
};

export default connect(null, mapDispatchToProps)(CreateCollection);
